import { API_BASE } from '../config';
import type { LeaderboardRecord } from './types';

/** Online leaderboard client (API_BASE: same-origin /api, or absolute on the CrazyGames build). */
const BASE = API_BASE;

export interface OnlineRow {
  disciplineId: string;
  metricKind: 'time' | 'distance';
  metric: number;
  score: number;
  rank: number;
  playerName: string;
  athleteName: string;
  species: string;
  nationality: string;
  createdAt: number;
  gold?: number;
  silver?: number;
  bronze?: number;
}

/** Best-effort score submission — never throws, never blocks gameplay. */
export async function submitScore(rec: LeaderboardRecord): Promise<void> {
  try {
    await fetch(`${BASE}/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        disciplineId: rec.disciplineId,
        metricKind: rec.metricKind,
        metric: rec.metric,
        score: rec.score,
        rank: rec.rank,
        playerName: rec.playerName,
        athleteName: rec.athleteName,
        species: rec.species,
        nationality: rec.nationality,
        clientId: rec.clientId,
      }),
    });
  } catch {
    // Offline or no backend (e.g. local dev) — the local leaderboard still has it.
  }
}

/**
 * Fetch the global rows, or null if the backend is unreachable.
 * view 'best' (default) = one row per player (their best, medal-relevant); 'all' = every
 * measure ever recorded for the discipline (shows the depth of a player's results).
 */
export async function fetchLeaderboard(
  disciplineId: string,
  kind: 'time' | 'distance',
  limit = 15,
  view: 'best' | 'all' = 'best',
): Promise<OnlineRow[] | null> {
  try {
    const res = await fetch(`${BASE}/leaderboard?discipline=${encodeURIComponent(disciplineId)}&kind=${kind}&limit=${limit}&view=${view}`);
    if (!res.ok) return null;
    const json = (await res.json()) as { rows?: OnlineRow[] };
    return json.rows ?? null;
  } catch {
    return null;
  }
}
